/**
 * Eagerly bundles every localized dictionary generated by the Lokalise sync
 * (`i18n/localized/<locale>.json`) and resolves the best match for the active
 * culture tag. Uses an eager-bundle strategy so locale switches stay synchronous.
 *
 * Every message id in the codebase also carries an inline English
 * `defaultMessage`, so a missing file, missing key, or empty dictionary safely
 * falls back to English at render time — this loader must never throw.
 */
const localizedModules = import.meta.glob('../../../i18n/localized/*.json', { eager: true }) as Record<string, { default: Record<string, string> }>;

const NEUTRAL_LOCALE = 'en-US';

/** Culture tags used by the app that don't match a Lokalise lang_iso directly. */
const LOCALE_ALIASES: Record<string, string> = {

    // Legacy .NET neutral tags for Chinese; Lokalise emits zh-CN / zh-TW.
    'zh-CHS': 'zh-CN',
    'zh-CHT': 'zh-TW'
};

const dictionaries: Record<string, Record<string, string>> = {};

for (const [path, module] of Object.entries(localizedModules)) {

    const fileName = path.split('/').pop() ?? '';
    const locale = fileName.replace(/\.json$/, '');
    dictionaries[locale] = module.default ?? {};

}

const lookup = (locale: string) => dictionaries[locale]
    ?? Object.entries(dictionaries).find(([key]) => key.toLowerCase() === locale.toLowerCase())?.[1];

/**
 * Resolves the localized message dictionary for a culture tag.
 * Fallback chain: alias -> exact tag -> first dictionary sharing the language
 * prefix (e.g. "es" matches "es-MX") -> en-US -> empty.
 */
export const getMessages = (cultureTag?: string): Record<string, string> => {

    const tag = LOCALE_ALIASES[cultureTag ?? ''] ?? cultureTag ?? NEUTRAL_LOCALE;

    const exact = lookup(tag);
    if (exact) return exact;

    const language = tag.split('-')[0].toLowerCase();
    const prefixMatch = Object.entries(dictionaries)
        .find(([key]) => key.toLowerCase() === language || key.toLowerCase().startsWith(`${language}-`));
    if (prefixMatch) return prefixMatch[1];

    return lookup(NEUTRAL_LOCALE) ?? {};

};

/**
 * Normalizes an app culture tag into a valid BCP-47 locale for IntlProvider.
 * Legacy .NET tags like zh-CHT are not valid BCP-47 and would throw inside
 * Intl.* constructors, so aliases are applied and invalid tags fall back to en-US.
 */
export const getIntlLocale = (cultureTag?: string): string => {

    const tag = LOCALE_ALIASES[cultureTag ?? ''] ?? cultureTag ?? NEUTRAL_LOCALE;

    try {

        // Throws RangeError on structurally invalid BCP-47 tags
        Intl.NumberFormat.supportedLocalesOf(tag);
        return tag;

    } catch {

        return NEUTRAL_LOCALE;

    }

};
